import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AlertModal } from "@/components/generic/AlertModal";
import { SettingsContainer } from "@/components/layout/SettingsContainer";
import type { Configuration } from "@/types/campaign";
import { store } from "@/utils/jotai";
import {
  createFileRoute,
  Link,
  Outlet,
  useBlocker,
  useChildMatches,
} from "@tanstack/react-router";
import { atom, useAtom } from "jotai";
import { XIcon } from "lucide-react";
import { SetStateAction, useEffect, useMemo } from "react";

const newConfigurationsAtom = atom<Configuration[]>();

export const Route = createFileRoute(
  "/_dashboard/campaigns/setup/define/advanced",
)({
  component: RouteComponent,
  staticData: {
    crumb: "Avanceret",
    meta: {
      title: "Avanceret",
    },
  },
  beforeLoad: async ({ context }) => {
    await context.queryClient.ensureQueryData(
      context.appliedConfigurationSuggestionsQueryOptions,
    );

    return {
      useAdvancedConfigurations: () => {
        const [newCampaignState] = context.useNewCampaignState();

        const [_newConfigurations, _setNewConfigurations] = useAtom(
          newConfigurationsAtom,
        );

        return useMemo(
          () => ({
            newConfigurations:
              _newConfigurations || newCampaignState.configurations,
            setNewConfigurations: (action: SetStateAction<Configuration[]>) =>
              _setNewConfigurations((prev) =>
                typeof action === "function"
                  ? action(prev || newCampaignState.configurations)
                  : action,
              ),
          }),
          [
            _newConfigurations,
            _setNewConfigurations,
            newCampaignState.configurations,
          ],
        );
      },
    };
  },
  loader: () => ({
    showCampaignSetupBottomBar: false,
  }),
  onLeave: () => {
    store.set(newConfigurationsAtom, undefined);
  },
});

function RouteComponent() {
  const routeMatch = Route.useMatch();
  const childMatches = useChildMatches();

  const isContactChildRoute = useMemo(
    () =>
      childMatches.some(
        (match) =>
          match.routeId ===
          "/_dashboard/campaigns/setup/define/advanced/$quality/$category/contacts",
      ),
    [childMatches],
  );
  const { useNewCampaignState, useAdvancedConfigurations } =
    Route.useRouteContext();
  const [newCampaignState] = useNewCampaignState();
  const { newConfigurations, setNewConfigurations } =
    useAdvancedConfigurations();

  useEffect(() => {
    setNewConfigurations(newCampaignState.configurations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCampaignState.configurations]);

  const isDirty = useMemo(() => {
    return (
      JSON.stringify(newCampaignState.configurations) !==
      JSON.stringify(newConfigurations)
    );
  }, [newCampaignState.configurations, newConfigurations]);

  const { proceed, reset, status } = useBlocker({
    disabled: !isDirty,
    shouldBlockFn: ({ next }) => {
      if (!isDirty) return false;
      // Allow free navigation within the advanced sub-tree.
      if (
        next.routeId === routeMatch.routeId ||
        next.routeId.startsWith(`${routeMatch.routeId}/`)
      ) {
        return false;
      }
      return true;
    },
    withResolver: true,
  });

  return (
    <>
      <SettingsContainer>
        <div className="flex h-full flex-col gap-y-10">
          <div className="flex items-center justify-between">
            <Breadcrumbs size="sm" from="/campaigns/setup/define" />

            <Link
              to={
                isContactChildRoute ? "/campaigns/setup/define/advanced" : ".."
              }
            >
              <XIcon className="size-5" />
            </Link>
          </div>

          <div className="flex h-full flex-col justify-center">
            <Outlet />
          </div>
        </div>
      </SettingsContainer>

      {status === "blocked" && (
        <AlertModal
          title="Du har ikke-gemte ændringer"
          description="Dine ændringer til variationerne vil gå tabt. Er du sikker på at du vil fortsætte?"
          onConfirm={proceed}
          onClose={reset}
        />
      )}
    </>
  );
}
